import {Component, OnInit, OnDestroy, ViewChild, TemplateRef, ChangeDetectorRef} from '@angular/core';
import {CoreService} from "../../shared/services/core.service";
import {ProjectsService} from "../../shared/services/projects.service";
import {ToastrService} from "ngx-toastr";
import {ActivatedRoute, Router} from "@angular/router";
import {ThemesService} from "../../shared/services/themes.service";
import {TranslateService} from "@ngx-translate/core";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {Project} from "../../shared/models/project.model";
import {FormGroup, FormBuilder, Validators} from "@angular/forms";
import {FormDataService} from "../../shared/services/form-data.service";
import {SessionsService} from "../../shared/services/sessions.service";
import {RegistrationsService} from "../../shared/services/registrations.service";
import {FilesService} from "../../shared/services/files.service";
import {BsModalService, BsModalRef} from 'ngx-bootstrap/modal';

interface StreitwertField {
  type: string;
  fieldName: string;
  spaLabel: string;
  label?: string;
  required: boolean;
  mandatory?: boolean;
  options?: Array<{option: string; value: string; entityLogicalName?: string}>;
  min?: string;
  max?: string;
  maxLength?: number;
  multiselect?: boolean;
  order?: number;
  availableForUpdateIfHasValue?: boolean;
  displayForExistingAkt?: boolean;
  displayForExistingStreitwert?: boolean;
  phoneNumber?: boolean;
  email?: boolean;
  url?: boolean;
}

interface StreitwertProduct {
  selectedFields: StreitwertField[];
}

interface StreitwertConfiguration {
  selectedProducts: StreitwertProduct[];
}

interface FormattedField {
  type: string;
  fieldName: string;
  label: string;
  spaLabel: string;
  maxLength?: string;
  min?: string;
  max?: string;
  precision?: string;
  order: number;
  currency?: Array<{
    name: string;
    id: string;
    symbol: string;
    isoName: string;
  }>;
  multiselect: boolean;
  options?: Array<{
    option: string;
    value: string;
    entityLogicalName?: string;
  }>;
  value: string;
  availableForUpdateIfHasValue: boolean;
  displayForExistingAkt: boolean;
  displayForExistingStreitwert: boolean;
  valueToDisplay?: string;
  mandatory: boolean;
  phoneNumber: boolean;
  email: boolean;
  url: boolean;
}

interface FormattedProduct {
  id: string;
  streitwertId: string;
  logicalName: string;
  name: string;
  streitwertNumber: number;
  selectedFields: FormattedField[];
}

interface FormattedData {
  projectShortName: string;
  aktFields: FormattedField[];
  claimFields: FormattedProduct[];
  sessionId: string;
  finishPostal: boolean;
}

interface FieldGroup {
  SelectedFields: Array<{
    Type: string;
    FieldName: string;
    Name: string;
    SpaLabel: string;
    Label: string;
    Required: boolean;
    Mandatory: boolean;
    Options: any[];
    Min?: string;
    Max?: string;
    MaxLength?: number;
    Multiselect: boolean;
    Order: number;
    AvailableForUpdateIfHasValue: boolean;
    DisplayForExistingAkt: boolean;
    DisplayForExistingStreitwert: boolean;
    phoneNumber: boolean;
    email: boolean;
    url: boolean;
  }>;
}

@Component({
  selector: 'app-values-in-dispute',
  templateUrl: './values-in-dispute.component.html',
  styleUrls: ['./values-in-dispute.component.sass']
})
export class ValuesInDisputeComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  public projectShortName: string = '';
  public project: Project = new Project();
  public loading = true;
  public loadingRedirect = false;
  public skipValuesInDisputeForm = false;
  public tokenIdentifier: string = '';
  public sessionToken: string = '';
  public form!: FormGroup;
  public valueInDisuputeRows: any[] = [];
  public fields: FieldGroup = { SelectedFields: [] };
  public dynamicForm: FormGroup = new FormGroup({});
  public showErrorMessageProjectNotActive = false;
  public projectSettings: any = {};
  public existingFiles: any[] = [];
  public productSelection: any[] = [];
  public productSelectionOptions: any[] = [];
  public enableMultipleProducts = false;
  public aktFormData: any = null;
  private disputeBoxValidationStates: Map<number, boolean> = new Map();
  public openAccordionId: number | null = null;
  public collapseDisputeBoxes: boolean[] = [];

  @ViewChild('deleteDisputeConfirmModal') deleteDisputeConfirmModal!: TemplateRef<any>;
  private modalRef?: BsModalRef;
  private rowToDelete: number | null = null;

  constructor(
    private coreService: CoreService,
    private projectService: ProjectsService,
    private toastrService: ToastrService,
    private activatedRoute: ActivatedRoute,
    private themeService: ThemesService,
    private router: Router,
    private formDataService: FormDataService,
    private translationService: TranslateService,
    private sessionService: SessionsService,
    private fb: FormBuilder,
    private registrationService: RegistrationsService,
    private filesService: FilesService,
    private modalService: BsModalService,
    private cdr: ChangeDetectorRef
  ) {
    this.translationService.setDefaultLang('de');
    this.initializeForms();

    // Set up refresh detection
    const currentPath = window.location.pathname;
    const lastPath = localStorage.getItem('lastPath');

    if (currentPath === lastPath) {
      // This is a refresh
      localStorage.setItem('isRefreshing', 'true');
    }

    localStorage.setItem('lastPath', currentPath);
  }

  private initializeForms(): void {
    // Main form for the component
    this.form = this.fb.group({
      valueInDispute: ['', [Validators.required, Validators.min(0)]]
    });

    // Dynamic form for dispute boxes
    this.dynamicForm = this.fb.group({});

    // Initialize fields array as empty
    this.fields = { SelectedFields: [] };
  }

  ngOnInit() {
    const projectShortNameParam = this.activatedRoute.snapshot.paramMap.get('projectShortname');
    this.tokenIdentifier = this.activatedRoute.snapshot.paramMap.get('tokenIdentifier') || '';
    this.sessionToken = this.activatedRoute.snapshot.queryParamMap.get('sessionId') || '';

    // Check if this is a page refresh
    const isRefreshing = localStorage.getItem('isRefreshing') === 'true';
    const isFromStep2 = sessionStorage.getItem('fromStep2') === 'true';



    if (projectShortNameParam) {
      this.projectShortName = projectShortNameParam.toUpperCase();

      // Check if step2 is completed by checking the form data
      const actFormData = this.formDataService.getActFormDataSnapshot();
      if (!actFormData) {

        // Redirect to step2 without sessionId
        this.router.navigate(['/registration', this.projectShortName, 'step2', this.tokenIdentifier]);
        return;
      }

      // Clear refresh flag
      localStorage.removeItem('isRefreshing');

      // Get project details
      this.projectService.getProject(this.projectShortName).pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe({
        next: (project: Project) => {
          this.themeService.checkThemeSettings(this.projectShortName);
          this.projectService.setLoading(true);
          this.project = project;
          this.projectService.setProject(project);
          const isProjectActive = this.projectService.isProjectActive();

          if (!isProjectActive) {
            this.showErrorMessageProjectNotActive = true;
          } else {
            // Initialize fields from streitwertConfiguration
            this.registrationService.getValue(this.projectShortName, this.tokenIdentifier).pipe(
              takeUntil(this.ngUnsubscribe)
            ).subscribe((Registration: any) => {
              if (Registration?.result?.project?.settings) {
                const settings = Registration.result.project.settings;
                // Only keep the required settings
                this.projectSettings = {
                  streitwertConfiguration: settings.streitwertConfiguration,
                  streitwertRequiredFileCategories: settings.streitwertRequiredFileCategories,
                  aktConfiguration: settings.aktConfiguration,
                  redirectToStep3: settings.redirectToStep3,
                  step3: settings.step3,
                  step4: settings.step4,
                  stepValuesInDispute: settings.stepValuesInDispute,
                  stepContract: settings.stepContract
                };

                const aktFormData = this.formDataService.getActFormDataSnapshot();
                if (aktFormData) {
                  this.aktFormData = aktFormData;
                }

                if (this.shouldSkipValuesInDisputeForm()) {
                  this.skipValuesInDisputeForm = true;
                  this.loadingRedirect = true;
                  this.coreService.enableGlobalLoading();
                  this.saveValuesAndContinue();
                  return;
                }

                const streitwertConfig = this.projectSettings.streitwertConfiguration;
                if (streitwertConfig?.selectedProducts) {
                  if(streitwertConfig.selectedProducts.length === 1) {
                    // Single product mode
                    const selectedProduct = streitwertConfig.selectedProducts[0];
                    this.fields = {
                      SelectedFields: selectedProduct.selectedFields.map((field: any) => ({
                        Type: field.type,
                        FieldName: field.fieldName,
                        Name: `${field.fieldName}_${Date.now()}`,
                        SpaLabel: field.spaLabel,
                        Label: field.label || field.spaLabel,
                        Required: field.required,
                        Mandatory: field.mandatory || field.required,
                        Options: field.options || [],
                        Min: field.min,
                        Max: field.max,
                        MaxLength: typeof field.maxLength === 'string' ? parseInt(field.maxLength, 10) : field.maxLength,
                        Multiselect: field.multiselect || false,
                        Order: field.order || 0,
                        AvailableForUpdateIfHasValue: field.availableForUpdateIfHasValue || false,
                        DisplayForExistingAkt: field.displayForExistingAkt || false,
                        DisplayForExistingStreitwert: field.displayForExistingStreitwert || false,
                        phoneNumber: field.phoneNumber || false,
                        email: field.email || false,
                        url: field.url || false
                      })).sort((a: any, b: any) => (a.Order || 0) - (b.Order || 0))
                    };
                    // Add initial value in dispute after fields are initialized
                    this.addValueInDispute(true);
                  } else if(streitwertConfig.selectedProducts.length > 1) {
                    this.productSelectionOptions = streitwertConfig.selectedProducts.map((product: any) => ({
                      streitwertProduct: product,
                      selectedFields: product.selectedFields.map((field: any) => ({
                        type: field.type,
                        fieldName: field.fieldName,
                        spaLabel: field.spaLabel,
                        label: field.label,
                        required: field.required,
                        mandatory: field.mandatory,
                        options: field.options || [],
                        min: field.min,
                        max: field.max,
                        maxLength: field.maxLength,
                        multiselect: field.multiselect || false,
                        order: field.order || 0,
                        availableForUpdateIfHasValue: field.availableForUpdateIfHasValue || false,
                        displayForExistingAkt: field.displayForExistingAkt || false,
                        displayForExistingStreitwert: field.displayForExistingStreitwert || false,
                        phoneNumber: field.phoneNumber || false,
                        email: field.email || false,
                        url: field.url || false
                      }))
                    }));
                    this.enableMultipleProducts = true;
                    // Add initial value in dispute after fields are initialized
                    this.addValueInDispute(true);
                  }
                }

                // Get existing files from the API
                if (this.sessionToken) {
                  this.filesService.getFiles(this.projectShortName, this.tokenIdentifier, this.sessionToken)
                    .pipe(takeUntil(this.ngUnsubscribe))
                    .subscribe((files: any) => {
                      // Ensure files.result is an array before filtering
                      const filesArray = Array.isArray(files.result) ? files.result : [];
                      // Filter files where isClaimFile is true and claimNumber matches the dispute box index
                      this.existingFiles = filesArray.filter((file: any) => {
                        if (!file.isClaimFile) return false;
                        // Extract the number from claimNumber (assuming format like "STRW-1")
                        const claimNumberMatch = file.claimNumber?.match(/STRW-(\d+)/);
                        if (!claimNumberMatch) return false;
                        const claimNumber = parseInt(claimNumberMatch[1], 10);
                        // Get the index of this dispute box (1-based)
                        const disputeBoxIndex = this.valueInDisuputeRows.findIndex(row => row.id === file.sectionId) + 1;
                        return claimNumber === disputeBoxIndex;
                      });
                    });
                }

              } else {
                this.toastrService.error('No project settings found');
              }

              this.coreService.disableGlobalLoading();
              this.loading = false;
            });
          }
        },
        error: (err: Error) => {
          this.toastrService.error(err.message);
        }
      });
    }

    // Subscribe to form changes
    this.subscribeToValueChanges();
  }

  // Custom validator for boolean fields that accepts "0" and "1" as valid values
  booleanRequiredValidator(control: any) {
    if (!control.value || control.value === '') {
      return { required: true };
    }
    // Accept "0" (Nein) and "1" (Ja) as valid values
    if (control.value === '0' || control.value === '1') {
      return null; // Valid
    }
    return null; // Valid for any other value
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  public checkValueInDisputeValid(): boolean {
    // Check if we have at least one value in dispute
    const hasValues = this.valueInDisuputeRows.length > 0;
    if (!hasValues) return false;

    // Check if all dispute boxes are valid
    return this.valueInDisuputeRows.every(row => {
      // First check if files are required and valid
      if (this.projectSettings?.streitwertRequiredFileCategories?.length > 0) {
        const filesValid = this.checkFilesForSection(row.id);
        if (!filesValid) return false;
      }

      // If there's only one product, validate its fields directly
      if (this.productSelectionOptions?.length === 1) {
        const singleProduct = this.productSelectionOptions[0];
        return singleProduct.selectedFields.every((field: any) => {
          if (!field.required) return true;

          const controlName = `${field.fieldName}_${row.id}`;
          const control = this.dynamicForm.get(controlName);

          // A field is valid if:
          // 1. The control exists
          // 2. The control has a value (including "0" for boolean fields)
          // 3. The control is valid
          if (!control) return false;
          
          // For boolean fields, "0" and "1" are valid values
          if (field.type === 'boolean') {
            return control.valid && (control.value === '0' || control.value === '1');
          }
          
          // For other fields, check for non-empty values
          return control.valid && (control.value !== null && control.value !== undefined && control.value !== '');
        });
      }

      if (this.enableMultipleProducts) {
        // For multiple products, first check if a product is selected
        const productControlName = `Product_${row.id}`;
        const productControl = this.dynamicForm.get(productControlName);
        if (!productControl || !productControl.value) return false;

        // Find the selected product
        const selectedProduct = this.productSelectionOptions.find(
          (option: any) => option.streitwertProduct.id === productControl.value
        );
        if (!selectedProduct) return false;

        // Check all fields for the selected product
        return selectedProduct.selectedFields.every((field: any) => {
          if (!field.required) return true;

          const controlName = `${field.fieldName}_${row.id}`;
          const control = this.dynamicForm.get(controlName);

          // A field is valid if:
          // 1. The control exists
          // 2. The control has a value (including "0" for boolean fields)
          // 3. The control is valid
          if (!control) return false;
          
          // For boolean fields, "0" and "1" are valid values
          if (field.type === 'boolean') {
            return control.valid && (control.value === '0' || control.value === '1');
          }
          
          // For other fields, check for non-empty values
          return control.valid && (control.value !== null && control.value !== undefined && control.value !== '');
        });
      }

      // For single product, check all fields in the fields array
      return this.fields.SelectedFields.every((field: any) => {
        if (!field.Required) return true;

        const controlName = `${field.FieldName}_${row.id}`;
        const control = this.dynamicForm.get(controlName);

        // A field is valid if:
        // 1. The control exists
        // 2. The control has a value (including "0" for boolean fields)
        // 3. The control is valid
        if (!control) return false;
        
        // For boolean fields, "0" and "1" are valid values
        if (field.Type === 'boolean') {
          return control.valid && (control.value === '0' || control.value === '1');
        }
        
        // For other fields, check for non-empty values
        return control.valid && (control.value !== null && control.value !== undefined && control.value !== '');
      });
    });
  }

  private checkFilesForSection(sectionId: number): boolean {
    // Check required file categories
    if (this.projectSettings?.requiredFileCategories?.length > 0) {
      const requiredFilesValid = this.projectSettings.requiredFileCategories.every((category: any) => {
        // Get the file control for this category
        const fileControlName = `Files_${category.id}`;
        const fileControl = this.dynamicForm.get(fileControlName);

        if (!fileControl) {
          return category.minimumFiles === 0; // If no minimum required, consider valid
        }

        const fileValue = fileControl.value;

        // Count both uploaded and existing files
        let totalFiles = 0;

        // Check uploaded files from the form control
        if (fileValue && typeof fileValue === 'object') {
          const uploadedFiles = fileValue.files || (fileValue as any).uploadedFiles || [];
          totalFiles += Array.isArray(uploadedFiles) ? uploadedFiles.length : 0;
        }

        // Check existing files from the API
        if (this.existingFiles?.length > 0) {
          const existingFiles = this.existingFiles.filter((file: any) => {
            const isCorrectCategory = file.fileCategoryId === category.id;
            const isValueInDispute = file.valueInDispute === false; // Files for act
            const isClaimFile = file.isClaimFile === false; // Files for act
            return isCorrectCategory && isValueInDispute && isClaimFile;
          });
          totalFiles += existingFiles.length;
        }

        return totalFiles >= category.minimumFiles;
      });

      if (!requiredFilesValid) return false;
    }

    // Check streitwert required file categories
    if (this.projectSettings?.streitwertRequiredFileCategories?.length > 0) {
      const streitwertFilesValid = this.projectSettings.streitwertRequiredFileCategories.every((category: any) => {
        // Get the file control for this category
        const fileControlName = `Files_${category.id}_${sectionId}`;
        const fileControl = this.dynamicForm.get(fileControlName);

        if (!fileControl) {
          return category.minimumFiles === 0; // If no minimum required, consider valid
        }

        const fileValue = fileControl.value;

        // Count both uploaded and existing files
        let totalFiles = 0;

        // Check uploaded files from the form control
        if (fileValue && typeof fileValue === 'object') {
          const uploadedFiles = fileValue.files || (fileValue as any).uploadedFiles || [];
          totalFiles += Array.isArray(uploadedFiles) ? uploadedFiles.length : 0;
        }

        // Check existing files from the API
        if (this.existingFiles?.length > 0) {
          const existingFiles = this.existingFiles.filter((file: any) => {
            const isCorrectCategory = file.fileCategoryId === category.id;
            const isValueInDispute = file.valueInDispute === true;
            const isClaimFile = file.isClaimFile === true;
            const isForThisSection = file.sectionId === sectionId;
            return isCorrectCategory && isValueInDispute && isClaimFile && isForThisSection;
          });
          totalFiles += existingFiles.length;
        }

        return totalFiles >= category.minimumFiles;
      });

      if (!streitwertFilesValid) return false;
    }

    return true;
  }

  public activeState(): boolean {
    // Get maxStreitwertCount from project settings, default to undefined if not set
    const maxStreitwertCount = this.projectSettings?.streitwertConfiguration?.maxStreitwertCount;

    // Check if all current dispute boxes are valid
    const allCurrentDisputeBoxesValid = this.areAllDisputeBoxesValid();

    // If maxStreitwertCount is not set, always show the button if current boxes are valid
    if (maxStreitwertCount === undefined) {
      return allCurrentDisputeBoxesValid;
    }

    // Show the button if:
    // 1. All current dispute boxes are valid AND
    // 2. Current count is below maxStreitwertCount
    const canAddMore = this.valueInDisuputeRows.length < maxStreitwertCount;

    return allCurrentDisputeBoxesValid && canAddMore;
  }

  public addValueInDispute(isInitialLoad: boolean = false): void {
    if (!isInitialLoad) {
      this.openAccordionId = null;
      this.cdr.detectChanges();
    }

    const newRow = {
      id: Date.now(), // Unique identifier for the row
      value: null,
      description: ''
    };

    this.valueInDisuputeRows.push(newRow);

    // Initialize validation state for the new dispute box as invalid
    const newIndex = this.valueInDisuputeRows.length - 1;
    this.disputeBoxValidationStates.set(newIndex, false);


    // Add controls to dynamic form based on field types
    if (this.fields && this.fields.SelectedFields) {
      this.fields.SelectedFields.forEach((field: any) => {
        const controlName = `${field.FieldName}_${newRow.id}`;

        switch (field.Type) {
          case 'text':
            this.dynamicForm.addControl(
              controlName,
              this.fb.control('', field.Required ? [Validators.required] : [])
            );
            break;
          case 'boolean':
            // Use custom validator for boolean fields that accepts "0" and "1" as valid
            const booleanValidators = field.Required ? [this.booleanRequiredValidator] : [];
            this.dynamicForm.addControl(
              controlName,
              this.fb.control('', booleanValidators)
            );
            break;
          case 'lookup':
            this.dynamicForm.addControl(
              controlName,
              this.fb.control('', field.Required ? [Validators.required] : [])
            );
            break;
          case 'date':
            this.dynamicForm.addControl(
              controlName,
              this.fb.control('', field.Required ? [Validators.required] : [])
            );
            break;
        }
      });
    }

    // Add Product control if multiple products are enabled
    if (this.enableMultipleProducts) {
      const productControlName = `Product_${newRow.id}`;

      // No default selection - user must choose a product
      this.dynamicForm.addControl(
        productControlName,
        this.fb.control('', [Validators.required])
      );
    }

    // Force change detection and validation update
    this.cdr.detectChanges();

    // Trigger validation update after a short delay to ensure controls are ready
    setTimeout(() => {
      this.updateOverallValidation();
    }, 150);

    // Only handle accordion states when adding new boxes (not during initial load)
    if (!isInitialLoad) {
      // Set the new accordion to be open
      this.openAccordionId = newRow.id;
      this.cdr.detectChanges();
    }
  }

  public removeValueInDispute(index: number): void {
    this.rowToDelete = index;
    this.modalRef = this.modalService.show(this.deleteDisputeConfirmModal, {
      class: 'modal-sm'
    });
  }

  public confirmDelete(): void {
    if (this.rowToDelete === null || this.rowToDelete >= this.valueInDisuputeRows.length) return;

    const row = this.valueInDisuputeRows[this.rowToDelete];
    const deletedIndex = this.rowToDelete;

    // Remove all form controls for this dispute box
    if (this.enableMultipleProducts) {
      // Get the selected product ID BEFORE removing the control
      const selectedProductId = this.dynamicForm.get(`Product_${row.id}`)?.value;

      // Remove product control
      this.dynamicForm.removeControl(`Product_${row.id}`);

      // Find and remove all field controls for the selected product
      if (selectedProductId) {
        const selectedProduct = this.productSelectionOptions.find(
          (option: any) => option.streitwertProduct.id === selectedProductId
        );
        if (selectedProduct) {
          selectedProduct.selectedFields.forEach((field: any) => {
            this.dynamicForm.removeControl(`${field.fieldName}_${row.id}`);
          });
        }
      }
    } else {
      // Remove all field controls from fields input
      this.fields.SelectedFields.forEach((field: any) => {
        this.dynamicForm.removeControl(`${field.FieldName}_${row.id}`);
      });
    }

    // Remove file controls
    if (this.projectSettings?.streitwertRequiredFileCategories?.length > 0) {
      this.projectSettings.streitwertRequiredFileCategories.forEach((category: any) => {
        this.dynamicForm.removeControl(`Files_${category.id}_${row.id}`);
      });
    }

    // Remove the row first
    this.valueInDisuputeRows.splice(deletedIndex, 1);

    // Create new validation states map with only valid indices
    const newValidationStates = new Map<number, boolean>();
    Array.from(this.disputeBoxValidationStates.entries())
      .filter(([index]) => index < deletedIndex) // Keep states before deleted index
      .forEach(([index, state]) => newValidationStates.set(index, state));

    Array.from(this.disputeBoxValidationStates.entries())
      .filter(([index]) => index > deletedIndex) // Move states after deleted index
      .forEach(([index, state]) => newValidationStates.set(index - 1, state));

    // Replace old validation states with new ones
    this.disputeBoxValidationStates = newValidationStates;

    // Clear openAccordionId if the deleted row was the open one
    if (this.openAccordionId === row.id) {
      this.openAccordionId = null;
    }

    // Trigger overall validation update
    this.updateOverallValidation();

    // Force change detection
    this.cdr.detectChanges();

    this.modalRef?.hide();
    this.rowToDelete = null;
  }

  public cancelDelete(): void {
    this.modalRef?.hide();
    this.rowToDelete = null;
  }

  public getValueInDisputeData(): Array<{
    fieldName: string;
    rowIndex: number;
    value: any;
    type: string;
    label: string;
  }> {
    const result: Array<{
      fieldName: string;
      rowIndex: number;
      value: any;
      type: string;
      label: string;
    }> = [];

    this.valueInDisuputeRows.forEach((row, rowIndex) => {
      // Add product selection if enabled
      if (this.enableMultipleProducts) {
        const productControlName = `Product_${row.id}`;
        const productControl = this.dynamicForm.get(productControlName);
        if (productControl && productControl.value) {
          result.push({
            fieldName: 'product',
            rowIndex: rowIndex,
            value: productControl.value,
            type: 'lookup',
            label: 'Product'
          });

          // Get the selected product's fields
          const selectedProduct = this.productSelectionOptions.find(
            (option: any) => option.streitwertProduct.id === productControl.value
          );

          if (selectedProduct) {
            selectedProduct.selectedFields.forEach((field: any) => {
              const controlName = `${field.fieldName}_${row.id}`;
              const control = this.dynamicForm.get(controlName);
              if (control) {
                result.push({
                  fieldName: field.fieldName,
                  rowIndex: rowIndex,
                  value: control.value,
                  type: field.type,
                  label: field.label || field.spaLabel
                });
              }
            });
          }
        }
      } else {
        // Handle single product case
        this.fields.SelectedFields.forEach((field: any) => {
          const controlName = `${field.FieldName}_${row.id}`;
          const control = this.dynamicForm.get(controlName);
          if (control) {
            result.push({
              fieldName: field.FieldName,
              rowIndex: rowIndex,
              value: control.value,
              type: field.Type,
              label: field.SpaLabel
            });
          }
        });
      }
    });

    return result;
  }

  private capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private formatLookupValue(field: any, value: string): string {
    if (!field.options || !Array.isArray(field.options)) {
      return value;
    }

    const selectedOption = field.options.find((opt: any) => opt.value === value);
    if (!selectedOption) {
      return value;
    }

    return JSON.stringify({
      Option: selectedOption.option,
      Value: selectedOption.value,
      EntityLogicalName: selectedOption.entityLogicalName
    });
  }

  public getFormattedData(): FormattedData {
    const result: FormattedData = {
      projectShortName: this.projectShortName,
      aktFields: [],
      claimFields: [],
      sessionId: this.sessionToken,
      finishPostal: false
    };

    // Get aktFields from form data service
    const aktFormData = this.formDataService.getActFormDataSnapshot();
    if (aktFormData) {
      // Get the akt configuration from project settings
      const aktConfig = this.projectSettings?.aktConfiguration?.selectedFields || [];

      // Map the fields according to the template structure
      result.aktFields = aktConfig.map((field: any) => {
        // Get the value from the form data
        let value = '';
        if (aktFormData && typeof aktFormData === 'object') {
          if (field.fieldName in aktFormData) {
            const rawValue = aktFormData[field.fieldName];

            // Format value based on field type
            if (field.type === 'lookup' || field.type === 'optionset') {
              const option = field.options?.find((opt: any) => opt.value === rawValue);
              if (option) {
                value = JSON.stringify({
                  Option: option.option,
                  Value: option.value,
                  EntityLogicalName: option.entityLogicalName
                });
              }
            } else if (field.type === 'boolean') {
              const option = field.options?.find((opt: any) => opt.value === rawValue);
              if (option) {
                value = JSON.stringify({
                  Option: option.option,
                  Value: option.value,
                  EntityLogicalName: option.entityLogicalName
                });
              }
            } else {
              value = rawValue || '';
            }
          }
        }

        return {
          type: field.type,
          fieldName: field.fieldName,
          label: field.label,
          spaLabel: field.spaLabel,
          maxLength: field.maxLength,
          min: field.min,
          max: field.max,
          precision: field.precision,
          order: field.order,
          currency: field.currency,
          multiselect: field.multiselect,
          options: field.options,
          value: value,
          availableForUpdateIfHasValue: field.availableForUpdateIfHasValue,
          displayForExistingAkt: field.displayForExistingAkt,
          displayForExistingStreitwert: field.displayForExistingStreitwert,
          valueToDisplay: field.valueToDisplay,
          mandatory: field.mandatory,
          phoneNumber: field.phoneNumber,
          email: field.email,
          url: field.url
        };
      });
    }

    // Get claim fields from each dispute box
    if (this.enableMultipleProducts) {
      // Multiple product mode
      this.valueInDisuputeRows.forEach((row, rowIndex) => {
        // Get the selected product ID from the form control
        const productControlName = `Product_${row.id}`;
        const productControl = this.dynamicForm.get(productControlName);
        const selectedProductId = productControl?.value;

        if (selectedProductId) {
          // Find the selected product from the options
          const selectedProduct = this.productSelectionOptions.find(
            (option: any) => option.streitwertProduct.id === selectedProductId
          );

          if (selectedProduct) {
            const product = {
              id: selectedProductId,
              streitwertId: selectedProductId,
              logicalName: selectedProduct.streitwertProduct.logicalName,
              name: selectedProduct.streitwertProduct.name,
              streitwertNumber: rowIndex,
              selectedFields: selectedProduct.selectedFields.map((field: any) => {
                const controlName = `${field.fieldName}_${row.id}`;
                const control = this.dynamicForm.get(controlName);
                const rawValue = control?.value || '';

                // Format value based on field type
                let value: any = '';
                if (field.type === 'lookup' || field.type === 'optionset') {
                  if (field.multiselect) {
                    // For multiselect, always ensure we have an array
                    let valuesArray: string[] = [];
                    if (Array.isArray(rawValue)) {
                      valuesArray = rawValue;
                    } else if (rawValue && rawValue !== '') {
                      // Single value, convert to array
                      valuesArray = [rawValue];
                    }
                    
                    // Convert array of values to array of objects and serialize
                    const arrayOfObjects = valuesArray.map((val: string) => {
                      const option = field.options?.find((opt: any) => opt.value === val);
                      return {
                        Option: option ? option.option : '',
                        Value: val,
                        EntityLogicalName: option ? option.entityLogicalName : null
                      };
                    });
                    
                    // Serialize the array as JSON string
                    value = JSON.stringify(arrayOfObjects);
                  } else {
                    // Single select
                    const option = field.options?.find((opt: any) => opt.value === rawValue);
                    if (option) {
                      value = JSON.stringify({
                        Option: option.option,
                        Value: option.value,
                        EntityLogicalName: option.entityLogicalName
                      });
                    }
                  }
                } else if (field.type === 'boolean') {
                  const option = field.options?.find((opt: any) => opt.value === rawValue);
                  if (option) {
                    value = JSON.stringify({
                      Option: option.option,
                      Value: option.value,
                      EntityLogicalName: option.entityLogicalName
                    });
                  }
                } else {
                  value = rawValue;
                }

                return {
                  type: field.type,
                  fieldName: field.fieldName,
                  label: field.label,
                  spaLabel: field.spaLabel,
                  maxLength: field.maxLength,
                  min: field.min,
                  max: field.max,
                  precision: field.precision,
                  order: field.order,
                  currency: field.currency,
                  multiselect: field.multiselect,
                  options: field.options,
                  value: value,
                  availableForUpdateIfHasValue: field.availableForUpdateIfHasValue,
                  displayForExistingAkt: field.displayForExistingAkt,
                  displayForExistingStreitwert: field.displayForExistingStreitwert,
                  valueToDisplay: field.valueToDisplay,
                  mandatory: field.mandatory,
                  phoneNumber: field.phoneNumber,
                  email: field.email,
                  url: field.url
                };
              })
            };
            result.claimFields.push(product);
          }
        }
      });
    } else {
      // Single product mode - iterate through all dispute boxes
      const selectedProduct = this.projectSettings?.streitwertConfiguration?.selectedProducts[0];
      if (selectedProduct && this.valueInDisuputeRows.length > 0) {
        this.valueInDisuputeRows.forEach((row, rowIndex) => {
          const product = {
            id: selectedProduct.id,
            streitwertId: selectedProduct.id,
            logicalName: selectedProduct.logicalName,
            name: selectedProduct.name,
            streitwertNumber: rowIndex,
            selectedFields: selectedProduct.selectedFields.map((field: any) => {
              const controlName = `${field.fieldName}_${row.id}`;
              const control = this.dynamicForm.get(controlName);
              const rawValue = control?.value || '';

              // Format value based on field type
              let value: any = '';
              if (field.type === 'lookup' || field.type === 'optionset') {
                if (field.multiselect) {
                  // For multiselect, always ensure we have an array
                  let valuesArray: string[] = [];
                  if (Array.isArray(rawValue)) {
                    valuesArray = rawValue;
                  } else if (rawValue && rawValue !== '') {
                    // Single value, convert to array
                    valuesArray = [rawValue];
                  }
                  
                  // Convert array of values to array of objects and serialize
                  const arrayOfObjects = valuesArray.map((val: string) => {
                    const option = field.options?.find((opt: any) => opt.value === val);
                    return {
                      Option: option ? option.option : '',
                      Value: val,
                      EntityLogicalName: option ? option.entityLogicalName : null
                    };
                  });
                  
                  // Serialize the array as JSON string
                  value = JSON.stringify(arrayOfObjects);
                } else {
                  // Single select
                  const option = field.options?.find((opt: any) => opt.value === rawValue);
                  if (option) {
                    value = JSON.stringify({
                      Option: option.option,
                      Value: option.value,
                      EntityLogicalName: option.entityLogicalName
                    });
                  }
                }
              } else if (field.type === 'boolean') {
                const option = field.options?.find((opt: any) => opt.value === rawValue);
                if (option) {
                  value = JSON.stringify({
                    Option: option.option,
                    Value: option.value,
                    EntityLogicalName: option.entityLogicalName
                  });
                }
              } else {
                value = rawValue;
              }

              return {
                type: field.type,
                fieldName: field.fieldName,
                label: field.label,
                spaLabel: field.spaLabel,
                maxLength: field.maxLength,
                min: field.min,
                max: field.max,
                precision: field.precision,
                order: field.order,
                currency: field.currency,
                multiselect: field.multiselect,
                options: field.options,
                value: value,
                availableForUpdateIfHasValue: field.availableForUpdateIfHasValue,
                displayForExistingAkt: field.displayForExistingAkt,
                displayForExistingStreitwert: field.displayForExistingStreitwert,
                valueToDisplay: field.valueToDisplay,
                mandatory: field.mandatory,
                phoneNumber: field.phoneNumber,
                email: field.email,
                url: field.url
              };
            })
          };
          result.claimFields.push(product);
        });
      }
    }

    return result;
  }

  // Update the subscribeToValueChanges method to use the new format
  public subscribeToValueChanges(): void {
    // Subscribe to dynamic form changes
    this.dynamicForm.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const data = this.getFormattedData();
      });

    // Subscribe to act form data changes
    this.formDataService.getActFormData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((formData: any) => {
        if (formData) {
          // Update the form data in the component
          this.aktFormData = formData;
          // Trigger a re-evaluation of the form data
          const data = this.getFormattedData();
        }
      });
  }

  public onSubmit(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.saveValuesAndContinue();
  }

  private shouldSkipValuesInDisputeForm(): boolean {
    return this.projectSettings?.step4 === false || this.projectSettings?.stepValuesInDispute === false;
  }

  private shouldSkipContract(): boolean {
    return this.projectSettings?.step3 === false
      || this.projectSettings?.stepContract === false
      || this.projectSettings?.redirectToStep3 === false;
  }

  private saveValuesAndContinue(): void {
    if (this.loadingRedirect && !this.skipValuesInDisputeForm) {
      return;
    }

    this.coreService.enableGlobalLoading();
    this.loadingRedirect = true;
    const formattedData = this.getFormattedData();

    this.registrationService.postValue(
      this.projectShortName,
      this.tokenIdentifier,
      formattedData
    )
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe({
      next: (response: any) => {
        if (response?.success) {
          const newTokenIdentifier = response?.result?.tokenIdentifier;
          this.navigateAfterSuccessfulSave(newTokenIdentifier);
        } else {
          this.toastrService.error(response?.result?.errorMessage || 'Error saving values in dispute');
          this.handleSaveError();
        }
      },
      error: (error: any) => {
        this.toastrService.error(error.message || 'Error saving values in dispute');
        this.handleSaveError();
      }
    });
  }

  private navigateAfterSuccessfulSave(newTokenIdentifier?: string): void {
    const navigationPromise = this.shouldSkipContract()
      ? this.router.navigate(['/registration', this.projectShortName, 'thank-you-2'])
      : this.router.navigate(['/registration', this.projectShortName, 'step4', newTokenIdentifier], {
          queryParams: { sessionId: this.sessionToken }
        });

    navigationPromise.then(() => {
      this.coreService.disableGlobalLoading();
      this.loadingRedirect = false;
    }).catch(() => {
      this.toastrService.error('Navigation failed');
      this.handleSaveError();
    });
  }

  private handleSaveError(): void {
    this.coreService.disableGlobalLoading();
    if (!this.skipValuesInDisputeForm) {
      this.loadingRedirect = false;
    }
  }

  onDisputeBoxValidationChange(isValid: boolean, disputeBoxIndex: number) {
    this.disputeBoxValidationStates.set(disputeBoxIndex, isValid);
    this.updateOverallValidation();
    setTimeout(() => {
      this.cdr?.detectChanges();
    }, 0);
  }

  getIsDisputeBoxOpen(value: { id: number }, index: number): boolean {
    if (this.openAccordionId === null) {
      return index === 0;
    }
    return this.openAccordionId === value.id;
  }

  onDisputeBoxOpenChange(boxId: number, isOpen: boolean): void {
    this.openAccordionId = isOpen ? boxId : null;
    this.cdr.detectChanges();
  }

  // Update the areAllDisputeBoxesValid method:
  private areAllDisputeBoxesValid(): boolean {
    const expectedBoxes = this.valueInDisuputeRows.length;
    const validationStates = Array.from(this.disputeBoxValidationStates.entries())
      .filter(([index]) => index < expectedBoxes); // Only consider states for existing boxes
    const trackedBoxes = validationStates.length;


    if (expectedBoxes === 0) {
      return false; // No dispute boxes means invalid form
    }

    // Check if we have validation states for all dispute boxes
    if (trackedBoxes !== expectedBoxes) {

      return false;
    }

    const allValid = validationStates.every(([_, isValid]) => isValid === true);


    return allValid;
  }

  // Fix the updateOverallValidation method:
  private updateOverallValidation(): void {
    const disputeBoxesValid = this.areAllDisputeBoxesValid();
    // Remove redundant checkValueInDisputeValid call - dispute boxes handle their own validation
    const overallValid = disputeBoxesValid;

    // Force change detection to update UI in the next tick
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  // Method to collapse a specific dispute box
  private collapseDisputeBox(disputeBoxIndex: number): void {
    // Initialize the array if needed
    if (!this.collapseDisputeBoxes) {
      this.collapseDisputeBoxes = new Array(this.valueInDisuputeRows.length).fill(false);
    }
    
    // Set the collapse flag for this specific dispute box
    this.collapseDisputeBoxes[disputeBoxIndex] = true;
    
    // Reset the flag after a short delay to allow the component to react
    setTimeout(() => {
      this.collapseDisputeBoxes[disputeBoxIndex] = false;
    }, 100);
  }

  // Add method to handle dispute box removal
  onDisputeBoxRemoved(disputeBoxIndex: number): void {

    // Remove the validation state for the removed dispute box
    this.disputeBoxValidationStates.delete(disputeBoxIndex);

    // Update overall validation
    this.updateOverallValidation();
  }

  public isFormValid(): boolean {
    // Since dispute boxes handle all validation internally, we just need to check:
    // 1. We have at least one dispute box
    // 2. All dispute boxes are valid

    const hasDisputeBoxes = this.valueInDisuputeRows.length > 0;
    const disputeBoxesValid = this.areAllDisputeBoxesValid();

    const overallValid = hasDisputeBoxes && disputeBoxesValid;


    return overallValid;
  }
}
